import { Plus } from "lucide-react";
import { Alert, Skeleton, Table } from "antd";
import { useNavigate } from "react-router-dom";

import { useInvoices } from "../../hooks/useInvoices";

import { Invoice } from "../../bitmaker/schemas/Billing/Invoice";

import { getInvoiceColumns } from "./InvoiceTableColumns";

export default function InvoicesList() {
  const navigate = useNavigate();

  const { invoices, loadingInvoices, fetchInvoicesError, refetchInvoices } =
    useInvoices();

  const openCreateEditor = () => {
    navigate("/billing/invoices/new");
  };

  const invoiceTableColumns = getInvoiceColumns({ showClient: true });

  const isInitialLoading = loadingInvoices && invoices.length === 0;

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Invoices</h2>
        <button
          type="button"
          className="btn btn-green"
          onClick={openCreateEditor}
          disabled={loadingInvoices}
        >
          <Plus size={16} />
          <span>Add Invoice</span>
        </button>
      </div>

      {/* Error */}
      {fetchInvoicesError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load invoices"
            description="Something went wrong while fetching data. Please try again."
            action={
              <button
                type="button"
                className="btn btn-gray"
                onClick={refetchInvoices}
                disabled={loadingInvoices}
              >
                Retry
              </button>
            }
          />
        </div>
      )}

      {/* Invoices Table */}
      <div className="rounded-2xl shadow-md p-5 bg-white text-gray-800 flex-1">
        <h3 className="text-lg font-semibold mb-4">All Invoices</h3>
        {isInitialLoading ? (
          <div className="mt-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="py-2">
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 1, width: "100%" }}
                />
              </div>
            ))}
          </div>
        ) : (
          <Table
            columns={invoiceTableColumns}
            dataSource={invoices}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            rowClassName={() => "cursor-pointer hover:bg-gray-50 transition-colors"}
            onRow={(record: Invoice) => ({
              onClick: () => navigate(`/billing/invoices/${record.id}`),
            })}
            loading={loadingInvoices}
            locale={{
              emptyText: fetchInvoicesError ? (
                "Unable to load invoices"
              ) : (
                <div className="py-10 flex flex-col items-center justify-center gap-3 text-center">
                  <div className="text-gray-500 text-sm">
                    You don't have any invoices yet
                  </div>
                  <button
                    type="button"
                    className="btn btn-gray"
                    onClick={openCreateEditor}
                    disabled={loadingInvoices}
                  >
                    <Plus size={16} />
                    <span>Create invoice</span>
                  </button>
                </div>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}
